import { useEffect, useState, type CSSProperties, type MouseEvent } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ImageOff,
  MessageSquarePlus,
  X,
} from 'lucide-react';
import { Spinner } from '../shared/Spinner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogClose, DialogDescription, DialogTitle } from '../ui/dialog';
import { VisuallyHidden } from 'radix-ui';
import { useImageComment, type ImageComment } from './ImageCommentContext';

export interface LightboxImage {
  src: string;
  alt: string;
  /**
   * The image's canonical URL, when it has one. Shown as an "Open original"
   * link so a downscaled or data-URL rendering still leads back to the file.
   */
  originalUrl?: string;
}

interface ImageLightboxProps {
  /**
   * Every image the opener has to offer. More than one turns the dialog into
   * a gallery: the viewer steps through them without closing and reopening.
   */
  images: LightboxImage[];
  /** Which image to show when the dialog opens. Defaults to the first. */
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called when the browser fails to load a full-size image (e.g. a presigned
   * URL expired), with the index of the image that failed.
   */
  onLoadError?: (index: number) => void;
  /**
   * Whether point commenting is on offer here, when a host provides a handler.
   * Defaults to `true`. Set it `false` for images the agent cannot see yet —
   * a draft attachment in the composer has not been sent, so a remark about it
   * would reach the agent before the image itself does.
   */
  commentable?: boolean;
}

const navButtonClass =
  'absolute top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white/70 transition-colors hover:bg-black/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-ring';

/** Where the reviewer pointed, as a fraction of the rendered image. */
interface Pin {
  x: number;
  y: number;
}

/**
 * The full-size image dialog shared by every image affordance — the inline
 * thumbnails in the chat stream (`ImagePreview`), the stacked images on a
 * user turn, the agent's own image output, and the clickable attachment chips
 * in the queue all open this same lightbox so "view the image" looks identical
 * wherever it's triggered.
 *
 * When the opener hands over several images the dialog pages through them
 * with the arrow controls or the arrow keys, so a message that carries three
 * screenshots is one viewer rather than three separate ones.
 *
 * Under an {@link ImageCommentProvider} the viewer also takes feedback:
 * clicking the image drops a pin and opens a composer, and sending hands the
 * remark plus the point it was aimed at to the host. Without a provider — or
 * with `commentable={false}`, for images the host knows are not addressable
 * yet — none of that renders.
 */
export function ImageLightbox({
  images,
  initialIndex = 0,
  open,
  onOpenChange,
  onLoadError,
  commentable = true,
}: ImageLightboxProps) {
  const count = images.length;
  const [active, setActive] = useState(initialIndex);
  const handler = useImageComment();
  const onComment = commentable ? handler : null;
  const [pin, setPin] = useState<Pin | null>(null);
  const [draft, setDraft] = useState('');
  // Intrinsic size of the image currently on screen, once the browser has
  // decoded it. Lets a comment carry pixel coordinates and not just a ratio.
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);

  // Reopening at a different image has to land on that image. A list that
  // changes size under an open dialog does not reset the position: the render
  // below clamps instead, so images resolving in the background never yank the
  // viewer away from what's being looked at.
  useEffect(() => {
    if (open) setActive(Math.max(initialIndex, 0));
  }, [open, initialIndex]);

  // A pin belongs to the image it was placed on, so closing the dialog or
  // paging to another image discards it along with anything typed.
  useEffect(() => {
    if (!open) {
      setPin(null);
      setDraft('');
    }
  }, [open]);

  const index = Math.min(active, Math.max(count - 1, 0));
  const current = images[index];
  if (!current) return null;

  const clearPin = () => {
    setPin(null);
    setDraft('');
  };

  const step = (delta: number) => {
    clearPin();
    setNatural(null);
    setActive((i) => (i + delta + count) % count);
  };

  /**
   * Place the pin where the image was clicked. Keyboard activation reports no
   * coordinates (`detail === 0`), so it lands in the middle: the composer is
   * still reachable without a pointer, and the reviewer can say in words which
   * part they mean.
   */
  const placePin = (e: MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (e.detail === 0 || rect.width === 0 || rect.height === 0) {
      setPin({ x: 0.5, y: 0.5 });
      return;
    }
    setPin({
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    });
  };

  const submitComment = () => {
    const text = draft.trim();
    if (!pin || !text || !onComment) return;
    const comment: ImageComment = {
      text,
      target: { src: current.src, alt: current.alt },
      fraction: pin,
      ...(natural
        ? {
            pixels: {
              x: Math.round(pin.x * natural.width),
              y: Math.round(pin.y * natural.height),
              width: natural.width,
              height: natural.height,
            },
          }
        : {}),
    };
    clearPin();
    onOpenChange(false);
    onComment(comment);
  };

  const footerLink = current.originalUrl;
  // The hint stays put once commenting is on offer, only changing what it
  // says: a row that appeared and vanished would resize the dialog under the
  // pin the moment the composer opened.
  const showFooter = Boolean(footerLink) || Boolean(onComment);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`w-auto max-w-[95vw] border-border/50 bg-black/95 p-0 sm:max-w-[95vw] ${
          // The dialog clips to its rounded border so the image corners follow
          // it. While a comment is being written that clipping is wrong: the
          // composer is anchored to a pin that can sit near an edge, and a
          // short image would slice the note in half. Let it escape for as
          // long as it is open.
          pin ? 'overflow-visible' : 'overflow-hidden'
        }`}
        showCloseButton={false}
        aria-describedby={undefined}
        // While the composer is open Escape belongs to it: the first press
        // drops the draft, only a second one closes the viewer.
        onEscapeKeyDown={(e) => {
          if (pin) {
            e.preventDefault();
            clearPin();
          }
        }}
        onKeyDown={(e) => {
          // Arrow keys page the gallery, but not while a comment is being
          // typed: there they move the caret.
          if (count < 2 || pin) return;
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            step(1);
          }
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            step(-1);
          }
        }}
      >
        <VisuallyHidden.Root>
          <DialogTitle>Image preview</DialogTitle>
          <DialogDescription>Full-size view of the attached image.</DialogDescription>
        </VisuallyHidden.Root>
        <DialogClose className="absolute right-2 top-2 z-10 rounded-sm bg-black/50 p-1 text-white/70 opacity-100 transition-colors hover:bg-black/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-ring">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
        <div className="relative">
          {onComment ? (
            <button
              type="button"
              onClick={placePin}
              aria-label="Comment on a spot in this image"
              className="block cursor-crosshair focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <LightboxImageEl
                image={current}
                onNaturalSize={setNatural}
                onError={() => onLoadError?.(index)}
              />
            </button>
          ) : (
            <LightboxImageEl
              image={current}
              onNaturalSize={setNatural}
              onError={() => onLoadError?.(index)}
            />
          )}
          {pin && onComment && (
            <CommentComposer
              pin={pin}
              value={draft}
              onChange={setDraft}
              onCancel={clearPin}
              onSubmit={submitComment}
            />
          )}
        </div>
        {count > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              className={`${navButtonClass} left-2`}
              onClick={() => step(-1)}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next image"
              className={`${navButtonClass} right-2`}
              onClick={() => step(1)}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <span className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white/80">
              {index + 1} of {count}
            </span>
          </>
        )}
        {showFooter && (
          <div className="flex w-full items-center justify-between gap-3 border-t border-border/30 px-3 py-2">
            {onComment ? (
              <span className="inline-flex items-center gap-1 text-xs text-white/60">
                <MessageSquarePlus className="h-3 w-3" />
                {pin
                  ? 'Enter sends the comment, Escape discards it'
                  : 'Click the image to comment on a spot'}
              </span>
            ) : (
              <span />
            )}
            {footerLink && (
              <a
                href={footerLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-status-info transition-colors hover:text-foreground"
              >
                <ExternalLink className="h-3 w-3" />
                Open original
              </a>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function clamp01(value: number) {
  return Math.min(Math.max(value, 0), 1);
}

function LightboxImageEl({
  image,
  onNaturalSize,
  onError,
}: {
  image: LightboxImage;
  onNaturalSize: (size: { width: number; height: number } | null) => void;
  onError: () => void;
}) {
  return (
    <img
      src={image.src}
      alt={image.alt}
      className="max-h-[90vh] max-w-[95vw] object-contain"
      onLoad={(e) => {
        const { naturalWidth, naturalHeight } = e.currentTarget;
        onNaturalSize(
          naturalWidth > 0 && naturalHeight > 0
            ? { width: naturalWidth, height: naturalHeight }
            : null,
        );
      }}
      onError={onError}
    />
  );
}

/**
 * The pin and its note, anchored over the point that was clicked. The card
 * opens toward the middle of the image (down-right from a pin in the top-left
 * corner, up-left from one in the bottom-right) so it never hangs off an edge.
 */
function CommentComposer({
  pin,
  value,
  onChange,
  onCancel,
  onSubmit,
}: {
  pin: Pin;
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const style: CSSProperties = {
    [pin.x < 0.5 ? 'left' : 'right']: `${(pin.x < 0.5 ? pin.x : 1 - pin.x) * 100}%`,
    [pin.y < 0.5 ? 'top' : 'bottom']: `${(pin.y < 0.5 ? pin.y : 1 - pin.y) * 100}%`,
  };

  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute z-20 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-status-info shadow-md"
        style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}
        data-testid="image-comment-pin"
      />
      <div
        className="absolute z-20 m-3 w-64 max-w-[80vw] rounded-md border border-border/60 bg-background p-2 shadow-lg"
        style={style}
        onKeyDown={(e) => {
          // Keep the gallery and the dialog out of what is being typed.
          e.stopPropagation();
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
      >
        <Textarea
          autoFocus
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Comment on this part of the image"
          placeholder="What should change here?"
          className="max-h-40 resize-y text-sm md:text-sm"
        />
        <div className="mt-1.5 flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" disabled={value.trim().length === 0} onClick={onSubmit}>
            Send
          </Button>
        </div>
      </div>
    </>
  );
}

interface ImagePreviewProps {
  src: string | null;
  alt: string;
  isLoading?: boolean;
  isExpired?: boolean;
  /** Called when the browser fails to load the image (e.g. presigned URL expired). */
  onLoadError?: () => void;
}

export function ImagePreview({ src, alt, isLoading, isExpired, onLoadError }: ImagePreviewProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const loadFailed = failedSrc !== null && failedSrc === src;

  if (isLoading) {
    return (
      <div
        role="status"
        className="flex h-24 w-[300px] items-center justify-center rounded-md border border-border bg-muted/30"
      >
        <Spinner size="md" className="text-muted-foreground" />
      </div>
    );
  }

  if (isExpired || !src || loadFailed) {
    return (
      <div className="flex h-24 w-[300px] items-center justify-center gap-2 rounded-md border border-border bg-muted/30">
        <ImageOff className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Image expired</span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="cursor-zoom-in rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="View full size"
      >
        <img
          src={src}
          alt={alt}
          className="max-w-[300px] rounded-md border border-border object-contain block"
          onError={() => {
            setFailedSrc(src);
            onLoadError?.();
          }}
        />
      </button>
      <ImageLightbox images={[{ src, alt }]} open={open} onOpenChange={setOpen} />
    </>
  );
}
